/**
 * stageTime — pure time-in-stage helpers for the pipeline board (SE-5497).
 *
 * A pipeline card surfaces how long it has sat in its current stage, and a
 * manager-configured per-stage target turns that into a "stale" signal. Both
 * derivations live here as pure functions so the AC rules are unit-tested in
 * one place and the renderers stay presentational.
 *
 * Rules (Pietra, 2026-07-16):
 * - `daysInStage = floor((now - enteredStageAt) / 86_400_000)` — whole 24h
 *   blocks from ELAPSED MILLISECONDS, so the count is timezone-independent
 *   (never calendar-day arithmetic).
 * - A card is stale when `daysInStage >= timeTargetDays` — INCLUSIVE at the
 *   target — with a positive target. A `timeTargetDays` of `0` or `null` means
 *   "no target configured" and never flags. A closed/terminal card never
 *   flags regardless of days-in-stage.
 * @module features/player-kanban/utils/stageTime
 */

/** Milliseconds in one 24-hour block. */
export const MS_PER_DAY = 86_400_000;

/**
 * Whole days a card has spent in its current stage.
 * @remarks
 * Computed from elapsed milliseconds and floored, so it is independent of the
 * viewer's timezone and of any offset encoded in `enteredStageAt`. A future
 * timestamp (clock skew) clamps to `0` rather than going negative. An absent or
 * unparseable timestamp yields `null` — the caller renders no indicator.
 * @param enteredStageAt - ISO timestamp of when the card entered its current stage.
 * @param now - Reference instant in epoch ms (defaults to `Date.now()`; injectable for tests).
 * @returns The floored 24h-block count, or `null` when the timestamp is missing/invalid.
 */
export const computeDaysInStage = (
  enteredStageAt: string | null | undefined,
  now: number = Date.now()
): number | null => {
  if (!enteredStageAt) return null;
  const entered = Date.parse(enteredStageAt);
  if (Number.isNaN(entered)) return null;
  const elapsed = now - entered;
  if (elapsed <= 0) return 0;
  return Math.floor(elapsed / MS_PER_DAY);
};

/** Inputs for {@link isStageStale}. */
export interface StageStaleInput {
  /** Whole days in stage (see {@link computeDaysInStage}); `null` when unknown. */
  readonly daysInStage: number | null;
  /** The stage's configured time target in days; `null`/`0` means "no target". */
  readonly timeTargetDays: number | null | undefined;
  /** Whether the card is closed/terminal (a Signed/Passed card never flags). */
  readonly isClosed?: boolean;
}

/**
 * Whether a card should show the stale flag.
 * @remarks
 * Stale iff the card is open, its days-in-stage is known, the stage has a
 * positive target, and `daysInStage >= timeTargetDays` (INCLUSIVE at the
 * target). Treating `0` (and any non-positive value) as "unset" implements the
 * lead-owned default that a `0` target never flags.
 * @param root0 - {@link StageStaleInput}.
 * @param root0.daysInStage - Whole days in stage, or `null` when unknown.
 * @param root0.timeTargetDays - The stage's configured target in days.
 * @param root0.isClosed - Whether the card is closed/terminal.
 * @returns `true` when the stale flag should render.
 */
export const isStageStale = ({
  daysInStage,
  timeTargetDays,
  isClosed = false,
}: StageStaleInput): boolean => {
  if (isClosed) return false;
  if (daysInStage == null) return false;
  if (timeTargetDays == null || timeTargetDays <= 0) return false;
  return daysInStage >= timeTargetDays;
};

/**
 * Parse a stage-editor time-target input into the value to persist (SE-5497).
 * @remarks
 * The lead-owned rules (decision 2): an empty field CLEARS the target (sends
 * `null`); any entered value is an integer floored to a minimum of 1 (there is
 * no maximum). A non-numeric OR partially-numeric string (`"14days"`, `"2e2"`)
 * is treated as a clear — parse the WHOLE value with `Number`, not
 * `parseInt`, so trailing garbage is rejected rather than silently truncated.
 * This keeps the "0 or empty = unset" contract consistent with
 * {@link isStageStale}.
 * @param raw - The raw text-field value.
 * @returns The integer target (>= 1), or `null` to clear the target.
 */
export const parseTimeTargetInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(1, Math.floor(parsed));
};
